import { SyntheticEvent } from 'react';
import { isArray, omit, some } from 'lodash';
import { SPEC, PROPS, VIEW, DATASOURCES, CONTAINER_TEMPLATES_WITH_DEFAULT_BG_COLOR } from '../constants';
import { DemoTag } from '../modules/components/Demo';
import { capitalizeFirstLetter } from './stringUtils';
import { isValidObject } from '.';
import { Registry } from '../../src/hooks/DashboardContext';
import { DashboardJson, EventData } from '../modules/models';

export function findTemplateByName<T extends { name?: string }>(
  TemplateRegistry?: ReadonlyArray<T>,
  name?: string,
): T | undefined {
  if (!name?.length || !TemplateRegistry?.length) {
    return undefined;
  }

  const lowerCaseTemplateName = name.toLowerCase();
  return TemplateRegistry.find(db => db.name?.toLowerCase() === lowerCaseTemplateName);
}

export const isTemplate = (TemplateRegistry: Registry[] | readonly Registry[] = [], name = ''): boolean => {
  if (typeof name !== 'string') {
    throw Object.assign(new Error(`Invalid template instantiation: ${JSON.stringify(name)}`), { stack: null });
  }
  return some(TemplateRegistry, db => db.name?.toLowerCase() === name?.toLowerCase());
};

export const isGridLayout = (name = ''): boolean => {
  return some(['RGLReponsiveGridLayout', 'RGLGridLayout'], db => db.toLowerCase() === name?.toLowerCase());
};

type ElementPartsProps = {
  [key: string]: any;
  className?: string;
};

export const getElementParts = (element: any[]) => {
  let instanceOf: any;
  let props: ElementPartsProps;
  let instanceKey: string | undefined;
  let eventListenerDef: any;
  let childrenDef: any[];

  if (Array.isArray(element)) {
    instanceOf = element[0];
    const shortProps = element[1] || {};
    props = Object.keys(shortProps).reduce<ElementPartsProps>((accum, key) => {
      if (key === '.') {
        accum.className = shortProps[key];
      } else {
        accum[key] = shortProps[key];
      }
      return accum;
    }, {});
    childrenDef = element[2] || [];
    eventListenerDef = element[3];
  } else {
    const reservedKeys = ['key', 'instanceOf', 'children', 'events'];
    instanceKey = element['key'];
    instanceOf = element['instanceOf'];
    childrenDef = element['children'] || [];
    eventListenerDef = element['events'];
    props = Object.keys(element).reduce<ElementPartsProps>((accum, key) => {
      if (!reservedKeys.includes(key)) {
        accum[key] = element[key];
      }
      return accum;
    }, {});
  }
  instanceKey = instanceKey ?? `i${instanceOf}`;
  return { instanceOf, instanceKey, props, eventListenerDef, childrenDef };
};

export const getViewElementParts = (viewElement: Record<string, unknown>): Record<string, any> => {
  let tag, props, children;
  if (Array.isArray(viewElement)) {
    [tag, props = {}, children = []] = viewElement;
  } else {
    const reservedKeys = ['key', 'instanceOf', 'events', 'children'];
    tag = viewElement['instanceOf'];
    children = viewElement['children'] ?? [];
    props = omit(viewElement, reservedKeys) ?? {};
  }
  return { tag, props, children };
};

export const getTemplateParts = (json: DashboardJson) => {
  const name = json.name ?? 'unnamed';
  const spec = json[SPEC] ?? {};
  const props = spec[PROPS] ?? {};
  const view = spec[VIEW] ?? [];
  const state = omit(spec, [PROPS, VIEW]) ?? {};
  const title = json.title ?? '';
  const datasources = spec[DATASOURCES];
  const description = (json.description as string) ?? '';
  const tags = (json.tags as unknown as DemoTag[]) ?? [];

  return {
    name,
    spec,
    props,
    state,
    view,
    title,
    datasources,
    description,
    tags,
  };
};

export const getTemplateView = (json: DashboardJson) => json[SPEC]?.[VIEW] ?? [];

export const extractEventDataFromEvent = (e: Event | SyntheticEvent<Element, Event>): EventData | Event => {
  if ('nativeEvent' in e) {
    const d: EventData = {
      currentTarget: {
        attributes: {},
      },
    };
    const target = e.currentTarget;

    if (target instanceof Element) {
      target.getAttributeNames().forEach(name => {
        d.currentTarget.attributes[name] = target.getAttribute(name);
      });
    }

    if (target instanceof HTMLInputElement) {
      d.currentTarget.value = target.value;
    }

    return d;
  }
  return e;
};

export const getCircularReplacer = () => {
  const seen = new WeakSet();
  return (key: any, value: object) => {
    if (isValidObject(value)) {
      if (seen.has(value)) {
        return;
      }
      seen.add(value);
    }
    return value;
  };
};

export const processTemplateProps = (
  props: { [x: string]: any },
  name: string,
  parent: string,
  tag: any,
  i: number,
  children: unknown,
) => {
  const newProps = isValidObject(props) ? { ...props } : {};
  if (!newProps.key) {
    newProps.key = `${name}-${parent}-${tag}-${i}`;
  }
  // rename '.' to className
  if (newProps['.'] !== undefined) {
    newProps.className = newProps['.'];
    delete newProps['.'];
  }

  // Include bg-inherit in className for all container elements without default background color if background color hasn't been set
  // This is needed to ensure child elements (e.g. table) can use parent container's background color
  if (
    !CONTAINER_TEMPLATES_WITH_DEFAULT_BG_COLOR.includes(name) &&
    isArray(children) &&
    Array.from(children).length > 0 &&
    newProps.className &&
    !String(newProps.className).includes('bg-')
  ) {
    newProps.className = newProps.className ? `bg-inherit ${newProps.className}` : 'bg-inherit';
  }

  // rename @ to on
  Object.keys(newProps).forEach(propKey => {
    if (propKey.startsWith('@') && typeof props[propKey] === 'function') {
      const eventCallback = props[propKey];
      const [at, eventName] = propKey.split('@');
      const htmlEventCallbackName = `on${capitalizeFirstLetter(eventName)}`;
      newProps[htmlEventCallbackName] = eventCallback;
      // deprecated
      console.warn(`${propKey} is deprecated. Use ${htmlEventCallbackName} instead`);
      delete newProps[propKey];
    }
  });

  return newProps;
};
